/**
 * Iter 204 PART C — Equipment Usage panel for Production Detail.
 * Real usage events per production_order. Read-only PDF via preview-first.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, Btn, Select, Input, Badge, Textarea } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { Wrench, FileText, Play, CheckCircle2, Trash2 } from "lucide-react";

export default function EquipmentUsagePanel({ productionOrderId, prodNo }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [open, setOpen] = useState(false);
  const [eqId, setEqId] = useState("");
  const [stage, setStage] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!productionOrderId) return;
    try {
      const r = await api.get(`/equipment/usage?production_order_id=${productionOrderId}`);
      setRows(Array.isArray(r) ? r : []);
    } catch { setRows([]); }
  }, [productionOrderId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/equipment").then(r => setEquipmentList(r.items || r || [])).catch(() => setEquipmentList([]));
  }, []);

  const start = async () => {
    if (!eqId) { alert("Ekipman seçin"); return; }
    setBusy(true);
    try {
      await api.post("/equipment/usage", {
        equipment_id: eqId, production_order_id: productionOrderId,
        stage: stage || null, notes: notes || null,
      });
      setEqId(""); setStage(""); setNotes(""); setOpen(false); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const complete = async (id) => {
    if (!confirm("Kullanım tamamlansın mı?")) return;
    await api.patch(`/equipment/usage/${id}/complete`, {});
    load();
  };
  const del = async (id) => {
    if (!confirm("Kayıt silinsin mi?")) return;
    await api.delete(`/equipment/usage/${id}`);
    load();
  };
  const dlPdf = async () => {
    try {
      const blob = await api.getBlob(`/equipment/usage/pdf?production_order_id=${productionOrderId}`);
      openPdfPreview(blob, `Ekipman_Kullanim_${prodNo || productionOrderId}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  return (
    <div className="mb-4" data-testid="equipment-usage-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase text-slate-600">Ekipman Kullanımı ({rows.length})</div>
        <div className="flex gap-1">
          <Btn size="sm" variant="secondary" onClick={() => setOpen(true)} data-testid="eu-open">
            <Wrench size={12}/> Ekipman Başlat
          </Btn>
          {rows.length > 0 && (
            <Btn size="sm" variant="ghost" onClick={dlPdf} data-testid="eu-pdf">
              <FileText size={12}/> PDF
            </Btn>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs italic text-slate-400 border border-dashed border-slate-200 rounded p-2">
          Bu üretim için ekipman kullanım kaydı yok. "Ekipman Başlat" ile gerçek olay kaydı ekleyin.
        </div>
      ) : (
        <table className="w-full text-xs border border-slate-200 rounded">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="text-left px-2 py-1">Kod</th><th className="text-left px-2 py-1">Ekipman</th>
            <th className="text-left px-2 py-1">Aşama</th><th className="text-left px-2 py-1">Operatör</th>
            <th className="text-left px-2 py-1">Başlangıç</th><th className="text-left px-2 py-1">Bitiş</th>
            <th className="text-left px-2 py-1">Durum</th><th className="text-left px-2 py-1">İşlem</th>
          </tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`eu-row-${r.id}`}>
              <td className="px-2 py-1 font-mono text-[10px]">{r.equipment_code_snapshot}</td>
              <td className="px-2 py-1">{r.equipment_name_snapshot}</td>
              <td className="px-2 py-1">{r.stage || "—"}</td>
              <td className="px-2 py-1">{r.operator_name}</td>
              <td className="px-2 py-1 font-mono text-[10px]">{(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="px-2 py-1 font-mono text-[10px]">{r.completed_at ? r.completed_at.slice(0, 16).replace("T", " ") : "—"}</td>
              <td className="px-2 py-1"><Badge level={r.status === "completed" ? "ok" : "warn"}>{r.status}</Badge></td>
              <td className="px-2 py-1">
                <div className="flex gap-1">
                  {r.status !== "completed" && <Btn size="sm" onClick={() => complete(r.id)} data-testid={`eu-complete-${r.id}`}><CheckCircle2 size={10}/></Btn>}
                  <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`eu-del-${r.id}`}><Trash2 size={10}/></Btn>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Ekipman Kullanımı Başlat">
        <div className="text-xs text-slate-500 mb-2">Operatör: <b>{user?.name || user?.username}</b> · Başlangıç: <b>şimdi</b></div>
        <div className="space-y-3">
          <Select label="Ekipman *" value={eqId} onChange={e => setEqId(e.target.value)} data-testid="eu-eq-select">
            <option value="">— Seçin —</option>
            {equipmentList.map(e => (
              <option key={e.id} value={e.id}>{e.code} — {e.name}</option>
            ))}
          </Select>
          <Input label="Aşama" value={stage} onChange={e => setStage(e.target.value)} placeholder="Tartım / Karıştırma / Dolum" data-testid="eu-stage"/>
          <Textarea label="Not" value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="eu-notes"/>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={start} disabled={busy} data-testid="eu-save"><Play size={12}/> Başlat</Btn>
        </div>
      </Modal>
    </div>
  );
}
